/**
 * SettingsPanel — 全参数定制弹窗（中级用户）+ 一键预设（初级用户）。
 * 所有修改即时写入 settingsStore 并持久化；主题/画质变更由订阅方响应。
 */
import {
  DEFAULT_THEME,
  THEME_ORDER,
  THEMES,
  exportTemplate,
  importTemplate,
} from '@shared/config/theme';
import { SUPPORTED_PLAYER_COUNTS } from '@shared/constants/camps';
import { $settings, resetSettings, updateSettings } from '../store/settingsStore';
import { resetTutorial } from '../tutorial/TutorialCoach';
import { tr, type DictKey } from './i18n';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  parent?: HTMLElement
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  parent?.appendChild(e);
  return e;
}

function seg<T extends string | number>(
  parent: HTMLElement,
  options: { value: T; label: string }[],
  current: T,
  onPick: (v: T) => void
): void {
  const wrap = el('div', 'seg', parent);
  for (const opt of options) {
    const b = el('button', `seg-btn${opt.value === current ? ' on' : ''}`, wrap);
    b.type = 'button';
    b.textContent = opt.label;
    b.addEventListener('click', () => {
      onPick(opt.value);
      render(parent.closest('.modal') as HTMLElement);
    });
  }
}

function sectionTitle(body: HTMLElement, key: DictKey, t: (k: DictKey) => string): void {
  const sec = el('div', 'section', body);
  el('div', 'sec-title', sec).textContent = t(key);
  // Mark last created section for rows
  (body as HTMLElement & { __last?: HTMLElement }).__last = sec;
}

function section(body: HTMLElement): HTMLElement {
  const host = (body as HTMLElement & { __last?: HTMLElement }).__last;
  return host ?? body;
}

function row(host: HTMLElement, label: string): HTMLElement {
  const r = el('div', 'row', host);
  el('span', 'lbl', r).textContent = label;
  return r;
}

function render(modal: HTMLElement): void {
  const s = $settings.get();
  const lang = s.language;
  const t = (k: DictKey) => tr(k, lang);
  const body = modal.querySelector('.modal-body') as HTMLElement;
  body.innerHTML = '';

  // ── 主题（一键模板） ──
  sectionTitle(body, 'theme', t);
  const themeHost = section(body);
  const themeRow = row(themeHost, '');
  themeRow.querySelector('.lbl')?.remove();
  const cards = el('div', 'seg', themeRow);
  cards.style.flex = '1';
  for (const id of THEME_ORDER) {
    const th = THEMES[id] ?? DEFAULT_THEME;
    const card = el('div', `theme-card${s.themeId === id ? ' on' : ''}`, cards);
    const name = el('div', 'tc-name', card);
    name.textContent = lang === 'zh' ? th.nameZh : th.nameEn;
    const sw = el('div', 'swatches', card);
    for (const c of [th.felt, th.wood, th.brass, th.room]) {
      const swatch = el('div', 'swatch', sw);
      swatch.style.background = c;
    }
    card.addEventListener('click', () => {
      updateSettings({ themeId: id });
      render(modal);
    });
  }

  // ── 画质 ──
  sectionTitle(body, 'quality', t);
  const qHost = section(body);
  seg(
    row(qHost, ''),
    (['low', 'medium', 'high'] as const).map((q) => ({
      value: q,
      label: q === 'low' ? t('qualityLow') : q === 'medium' ? t('qualityMed') : t('qualityHigh'),
    })),
    s.quality,
    (v) => updateSettings({ quality: v })
  );

  // ── 对局 ──
  sectionTitle(body, 'match', t);
  const mHost = section(body);
  seg(
    row(mHost, t('match')),
    [
      { value: 'ai' as const, label: t('modeAi') },
      { value: 'hotseat' as const, label: t('modeHotseat') },
    ],
    s.mode,
    (v) => updateSettings({ mode: v })
  );
  seg(
    row(mHost, t('players')),
    SUPPORTED_PLAYER_COUNTS.map((n) => ({ value: n, label: String(n) })),
    s.playerCount,
    (v) => updateSettings({ playerCount: v })
  );
  const diffRow = row(mHost, t('difficulty'));
  seg(
    diffRow,
    [
      { value: 'easy' as const, label: t('easy') },
      { value: 'medium' as const, label: t('medium') },
      { value: 'hard' as const, label: t('hard') },
    ],
    s.aiDifficulty,
    (v) => updateSettings({ aiDifficulty: v })
  );
  if (s.mode === 'ai' && s.playerCount !== 2) {
    const note = el('div', 'modal-sub', mHost);
    note.textContent = lang === 'zh' ? '人机模式建议 2 人对局' : 'AI mode suits 2 players best';
  }
  seg(
    row(mHost, t('timer')),
    [0, 15, 30, 60].map((n) => ({ value: n, label: n === 0 ? t('off') : `${n}${t('secUnit')}` })),
    s.turnTimerSec,
    (v) => updateSettings({ turnTimerSec: v })
  );

  // ── 声音 ──
  sectionTitle(body, 'audio', t);
  const aHost = section(body);
  const sfxRow = row(aHost, t('sfx'));
  const sfxRange = el('input', '', sfxRow) as HTMLInputElement;
  sfxRange.type = 'range';
  sfxRange.min = '0';
  sfxRange.max = '1';
  sfxRange.step = '0.05';
  sfxRange.value = String(s.sfxVolume);
  el('span', 'range-val', sfxRow).textContent = `${Math.round(s.sfxVolume * 100)}%`;
  const sfxVal = sfxRow.querySelector('.range-val') as HTMLElement;
  sfxRange.addEventListener('input', () => {
    updateSettings({ sfxVolume: Number(sfxRange.value) });
    sfxVal.textContent = `${Math.round(Number(sfxRange.value) * 100)}%`;
  });

  const ambRow = row(aHost, t('ambient'));
  const ambRange = el('input', '', ambRow) as HTMLInputElement;
  ambRange.type = 'range';
  ambRange.min = '0';
  ambRange.max = '1';
  ambRange.step = '0.05';
  ambRange.value = String(s.ambientVolume);
  el('span', 'range-val', ambRow).textContent = `${Math.round(s.ambientVolume * 100)}%`;
  const ambVal = ambRow.querySelector('.range-val') as HTMLElement;
  ambRange.addEventListener('input', () => {
    updateSettings({ ambientVolume: Number(ambRange.value) });
    ambVal.textContent = `${Math.round(Number(ambRange.value) * 100)}%`;
  });

  const muteRow = row(aHost, t('mute'));
  const muteSw = el('div', `switch${s.muted ? ' on' : ''}`, muteRow);
  muteSw.addEventListener('click', () => {
    updateSettings({ muted: !s.muted });
    render(modal);
  });

  // ── 界面 ──
  sectionTitle(body, 'interface', t);
  const iHost = section(body);
  seg(
    row(iHost, t('language')),
    [
      { value: 'zh' as const, label: '中文' },
      { value: 'en' as const, label: 'English' },
    ],
    s.language,
    (v) => updateSettings({ language: v })
  );
  const hintRow = row(iHost, t('hints'));
  const hintSw = el('div', `switch${s.showHints ? ' on' : ''}`, hintRow);
  hintSw.addEventListener('click', () => {
    updateSettings({ showHints: !s.showHints });
    render(modal);
  });

  const iriRow = row(iHost, t('iridescence'));
  const iriRange = el('input', '', iriRow) as HTMLInputElement;
  iriRange.type = 'range';
  iriRange.min = '0';
  iriRange.max = '1';
  iriRange.step = '0.05';
  iriRange.value = String(s.marbleIridescence);
  el('span', 'range-val', iriRow).textContent = `${Math.round(s.marbleIridescence * 100)}%`;
  const iriVal = iriRow.querySelector('.range-val') as HTMLElement;
  iriRange.addEventListener('input', () => {
    updateSettings({ marbleIridescence: Number(iriRange.value) });
    iriVal.textContent = `${Math.round(Number(iriRange.value) * 100)}%`;
  });

  const spdRow = row(iHost, t('speed'));
  const spdRange = el('input', '', spdRow) as HTMLInputElement;
  spdRange.type = 'range';
  spdRange.min = '0.5';
  spdRange.max = '2';
  spdRange.step = '0.25';
  spdRange.value = String(s.animationSpeed);
  el('span', 'range-val', spdRow).textContent = `${s.animationSpeed}×`;
  const spdVal = spdRow.querySelector('.range-val') as HTMLElement;
  spdRange.addEventListener('input', () => {
    updateSettings({ animationSpeed: Number(spdRange.value) });
    spdVal.textContent = `${Number(spdRange.value)}×`;
  });

  // ── 新手引导（v2.1 §12.1）：重置完成标记，下次开局重播教程。 ──
  const tutRow = row(iHost, '');
  tutRow.querySelector('.lbl')?.remove();
  const tutBtn = el('button', 'mini-btn', tutRow);
  tutBtn.type = 'button';
  tutBtn.textContent = t('tutReplay');
  tutBtn.addEventListener('click', () => {
    resetTutorial();
    modal.dispatchEvent(new CustomEvent('mc:toast', { detail: t('tutReplayOk') }));
  });

  // ── 配置模板 ──
  sectionTitle(body, 'template', t);
  const tpHost = section(body);
  const area = el('textarea', 'tpl-area', tpHost);
  area.placeholder = tr('importTpl', lang);
  const tpRow = row(tpHost, '');
  tpRow.querySelector('.lbl')?.remove();
  const exportBtn = el('button', 'mini-btn', tpRow);
  exportBtn.type = 'button';
  exportBtn.textContent = t('exportTpl');
  exportBtn.addEventListener('click', () => {
    area.value = exportTemplate($settings.get());
    area.select();
    void navigator.clipboard?.writeText(area.value).catch(() => undefined);
  });
  const importBtn = el('button', 'mini-btn', tpRow);
  importBtn.type = 'button';
  importBtn.textContent = t('apply');
  importBtn.addEventListener('click', () => {
    const parsed = importTemplate(area.value);
    if (parsed) {
      updateSettings(parsed);
      render(modal);
      modal.dispatchEvent(new CustomEvent('mc:toast', { detail: t('tplOk') }));
    } else {
      modal.dispatchEvent(new CustomEvent('mc:toast', { detail: t('tplBad') }));
    }
  });
  const resetBtn = el('button', 'mini-btn', tpRow);
  resetBtn.type = 'button';
  resetBtn.textContent = lang === 'zh' ? '恢复默认' : 'Reset';
  resetBtn.addEventListener('click', () => {
    resetSettings();
    render(modal);
  });
}

/**
 * Open the settings modal into `root`. Resolves `onDone` when closed.
 * Listens for 'mc:toast' events to surface feedback toasts via `toast`.
 */
export function openSettings(
  root: HTMLElement,
  toast: (text: string) => void,
  onDone: () => void
): void {
  const veil = el('div', 'modal-veil', root);
  const modal = el('div', 'modal', veil);
  const h = el('h2', '', modal);
  h.textContent = tr('settings', $settings.get().language);
  el('div', 'modal-sub', modal).textContent =
    $settings.get().language === 'zh'
      ? '预设一键即换，参数随调随现'
      : 'One-tap presets, full parametric control';
  el('div', 'modal-body', modal);
  const foot = el('div', 'modal-foot', modal);
  const done = el('button', 'btn btn-primary', foot);
  done.textContent = tr('done', $settings.get().language);
  done.addEventListener('click', close);

  modal.addEventListener('mc:toast', (e) => toast((e as CustomEvent<string>).detail));

  function close(): void {
    veil.remove();
    onDone();
  }
  veil.addEventListener('click', (e) => {
    if (e.target === veil) close();
  });
  render(modal);
}
